// Money is integer cents everywhere in this codebase. These helpers are the
// only sanctioned crossing points between cents and anything a human types or
// reads — nothing else should multiply or divide by 100.

export function formatMoney(cents: number, currency = 'EUR', locale = 'en-GB'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

/** Cents → the decimal string an <input> should show. */
export function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2)
}

/**
 * Parse whatever a human typed into cents. Accepts "1234.50", "1 234,50",
 * "1,234.50" and "€1.234,50"; returns null for anything unparseable so the
 * caller can keep the previous value instead of silently zeroing a price.
 */
export function parseMoneyInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  let cleaned = trimmed.replace(/[^\d.,-]/g, '').replace(/\s/g, '')
  const lastComma = cleaned.lastIndexOf(',')
  const lastDot = cleaned.lastIndexOf('.')

  if (lastComma !== -1 && lastDot !== -1) {
    // Both present: whichever comes last is the decimal separator.
    cleaned =
      lastComma > lastDot
        ? cleaned.replace(/\./g, '').replace(',', '.')
        : cleaned.replace(/,/g, '')
  } else if (lastComma !== -1) {
    // Comma only. Two digits after it means decimals; otherwise it groups.
    cleaned =
      cleaned.length - lastComma - 1 === 2
        ? cleaned.replace(',', '.')
        : cleaned.replace(/,/g, '')
  }

  const value = Number.parseFloat(cleaned)
  if (!Number.isFinite(value)) return null
  return Math.round(value * 100)
}

export function parseQuantityInput(raw: string): number | null {
  const cleaned = raw.trim().replace(/\s/g, '').replace(',', '.')
  if (!cleaned) return null
  const value = Number.parseFloat(cleaned)
  if (!Number.isFinite(value) || value < 0) return null
  return Math.round(value * 1000) / 1000
}

export function formatQuantity(quantity: number): string {
  // Trailing zeros on a whole quantity are noise on a customer-facing document.
  return Number.isInteger(quantity) ? String(quantity) : String(Number(quantity.toFixed(3)))
}

export function formatPercent(rate: number): string {
  return `${Number((rate * 100).toFixed(2))}%`
}

export function lineTotalCents(quantity: number, unitPriceCents: number): number {
  return Math.round(quantity * unitPriceCents)
}
